import {
  createProduct,
  listProducts,
  deleteProduct,
  updateProductStatus,
  listCategoriesByPointOfSaleId,
  getConvenienceFees,
} from "@/modules/product/service/product.service";
import {
  listCatalogProductsBySlug,
  listCatalogCategoriesBySlug,
} from "@/services/catalog/catalog.service";

const state = {
  pointOfSaleId: null,
  products: [],
  pagination: {
    page: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 12,
  },
  categories: [],
  searchTerm: "",
  convenienceFees: [],
};

const mutations = {
  SET_PRODUCTS(state, products) {
    state.products = products;
  },
  SET_PAGINATION(state, pagination) {
    if (pagination) {
      state.pagination = pagination;
    }
  },
  SET_CATEGORIES(state, categories) {
    state.categories = categories;
  },
  SET_SEARCH_TERM(state, searchTerm) {
    state.searchTerm = searchTerm;
  },
  SET_CONVENIENCE_FEES(state, convenienceFees) {
    state.convenienceFees = convenienceFees;
  },
};

const actions = {
  async fetchProducts({ commit, rootState, state }, filters) {
    try {
      const token = rootState.auth.token;

      if (!token) {
        console.error("Token não disponível para buscar produtos");
        return { hasMore: false };
      }

      const finalFilters = {
        ...filters,
        pointOfSaleId: filters.pointOfSaleId,
        searchTerm:
          typeof filters.searchTerm !== "undefined"
            ? filters.searchTerm
            : state.searchTerm,
      };

      if (typeof filters.searchTerm !== "undefined") {
        commit("SET_SEARCH_TERM", filters.searchTerm);
      }

      // Obtém o pointOfSaleId: prioriza o passado por parâmetro, senão busca do sessionStorage
      let pointOfSaleId = finalFilters.pointOfSaleId;

      if (!pointOfSaleId) {
        console.error("pointOfSaleId não disponível para buscar produtos");
        return { hasMore: false };
      }

      const response = await listProducts(finalFilters, token);
      if (response && response.response && response.response.items) {
        const { pagination } = response.response;
        let { items } = response.response;

        if (!Array.isArray(items)) items = [];

        const products = items.map((item) => ({
          ...item.product,
          productCategories: item.productCategories || [],
        }));

        // Se append=true, adiciona aos produtos existentes; senão substitui
        if (filters.append) {
          const existingProducts = state.products;
          const newProducts = [...existingProducts, ...products];
          commit("SET_PRODUCTS", newProducts);
        } else {
          commit("SET_PRODUCTS", products);
        }

        commit("SET_PAGINATION", pagination);

        // Retorna se há mais páginas, com base na paginação real do backend
        return {
          hasMore: pagination ? pagination.page < pagination.totalPages : false,
          pagination,
        };
      } else {
        console.error("A resposta da API não contém produtos válidos");
        return { hasMore: false };
      }
    } catch (error) {
      console.error("Erro ao carregar os produtos:", error);
      return { hasMore: false };
    }
  },

  async fetchCategoriesByPointOfSaleId(
    { commit, rootState, state },
    pointOfSaleId,
  ) {
    try {
      const token = rootState.auth.token;

      if (!token) {
        console.error("Token não disponível para buscar categorias");
        return;
      }

      let finalPointOfSaleId = pointOfSaleId;

      if (!finalPointOfSaleId) {
        const currentPointOfSale = JSON.parse(
          sessionStorage.getItem("currentPointOfSale") || "{}",
        );
        finalPointOfSaleId = currentPointOfSale.id || state.pointOfSaleId;
      }

      if (!finalPointOfSaleId) {
        console.error("pointOfSaleId não disponível para buscar categorias");
        return;
      }

      const response = await listCategoriesByPointOfSaleId(finalPointOfSaleId);
      if (response && response.response) {
        commit("SET_CATEGORIES", response.response);
      }
      return response;
    } catch (error) {
      console.error("Erro ao carregar as categorias:", error);
    }
  },

  /**
   * Storefront por SLUG (menuus-core-api). Mesmas mutações e mesmo mapeamento
   * de `fetchProducts` — a resposta de /catalog/:slug/products tem shape
   * idêntico ao legado. Separada para não afetar os consumidores admin de
   * `fetchProducts` (ProductsPage, product.vue, re-dispatch pós-delete).
   *
   * @param {{ slug: string, page?: number, append?: boolean, searchTerm?: string, categoryId?: string, categoryType?: string }} payload
   */
  async fetchCatalogProductsBySlug(
    { commit, state },
    { slug, page = 1, append = false, searchTerm, categoryId, categoryType },
  ) {
    try {
      if (!slug) {
        console.error("slug não disponível para buscar produtos do catálogo");
        return { hasMore: false };
      }

      if (typeof searchTerm !== "undefined") {
        commit("SET_SEARCH_TERM", searchTerm);
      }

      const response = await listCatalogProductsBySlug(slug, {
        page,
        searchTerm,
        categoryId,
        categoryType,
      });
      if (response && response.response && response.response.items) {
        let { items } = response.response;
        const { pagination } = response.response;

        if (!Array.isArray(items)) items = [];

        const products = items.map((item) => ({
          ...item.product,
          productCategories: item.productCategories || [],
        }));

        if (append) {
          commit("SET_PRODUCTS", [...state.products, ...products]);
        } else {
          commit("SET_PRODUCTS", products);
        }

        commit("SET_PAGINATION", pagination);

        return {
          hasMore: pagination ? pagination.page < pagination.totalPages : false,
          pagination,
        };
      }

      console.error("A resposta do catálogo não contém produtos válidos");
      return { hasMore: false };
    } catch (error) {
      console.error("Erro ao carregar os produtos do catálogo:", error);
      return { hasMore: false };
    }
  },

  /**
   * Storefront por SLUG — categorias. Mesma mutação `SET_CATEGORIES` de
   * `fetchCategoriesByPointOfSaleId`.
   *
   * @param {string} slug
   */
  async fetchCatalogCategoriesBySlug({ commit }, slug) {
    try {
      if (!slug) {
        console.error("slug não disponível para buscar categorias do catálogo");
        return;
      }

      const response = await listCatalogCategoriesBySlug(slug);
      if (response && response.response) {
        commit("SET_CATEGORIES", response.response);
      }
      return response;
    } catch (error) {
      console.error("Erro ao carregar as categorias do catálogo:", error);
    }
  },

  async fetchConvenienceFees({ commit, rootState }, payload) {
    const token = rootState.auth.token;
    if (!token) {
      console.error("Token não disponível para buscar taxas de conveniência");
      return;
    }

    let pointOfSaleId =
      typeof payload === "string"
        ? payload
        : payload?.pointOfSaleId;

    if (!pointOfSaleId) {
      const current = JSON.parse(
        sessionStorage.getItem("currentPointOfSale") || "{}",
      );
      pointOfSaleId = current.id;
    }

    if (!pointOfSaleId) {
      pointOfSaleId =
        sessionStorage.getItem("pdvId") || localStorage.getItem("pdvId");
    }

    if (!pointOfSaleId) {
      console.error(
        "pointOfSaleId não disponível para buscar taxas de conveniência",
      );
      return;
    }

    try {
      const response = await getConvenienceFees(pointOfSaleId);
      const fees = response?.response?.convenienceFees;
      if (fees) {
        commit("SET_CONVENIENCE_FEES", fees);
        try {
          sessionStorage.setItem("convenienceFees", JSON.stringify(fees));
        } catch (_) {}
      }
    } catch (error) {
      console.error("Erro ao carregar taxas de conveniência:", error);
    }
  },

  // Ação para adicionar um novo produto
  async addProduct({ commit }, productData) {
    try {
      const newProduct = await createProduct(productData); // Chamada da API para criar o produto
      commit("SET_PRODUCT", newProduct); // Comitando a mutação com o novo produto
    } catch (error) {
      console.error("Erro ao adicionar produto:", error);
    }
  },

  // Action para deletar produto
  async deleteProduct({ dispatch }, id) {
    try {
      await deleteProduct(id);
      // Atualiza a lista de produtos após deletar
      dispatch("fetchProducts");
    } catch (error) {
      console.error("Erro ao deletar produto:", error);
    }
  },

  // Action para atualizar status do produto
  async updateProductStatus({ dispatch }, { productId, status }) {
    try {
      await updateProductStatus(productId, status);
      dispatch("fetchProducts");
    } catch (error) {
      console.error("Erro ao atualizar status do produto:", error);
    }
  },
};

const getters = {
  getProducts: (state) => state.products, // Garantindo que está acessando corretamente os produtos
  getProductsPagination: (state) => state.pagination,
  getPointOfSaleId: (state) => state.pointOfSaleId, // Garantindo que está acessando corretamente o id do ponto de venda
  getCategories: (state) => state.categories, // Garantindo que está acessando corretamente as categorias
  getSearchTerm: (state) => state.searchTerm,
  getConvenienceFees: (state) => state.convenienceFees, // Garantindo que está acessando corretamente as taxas de conveniência
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
